import { useToast } from '@/hooks/useToast';
import { useCallback } from 'react';
import useDrivePicker from 'react-google-drive-picker';
import { utils as utilsCore } from '@industriousapps/excelkits-core';
import { useAuth } from '@/hooks/useAuth';
import logger from '@/utils/logger';
import { checkAccessToken, googleCloudTokenRefresh } from '@/services/auth/google';
import { firebaseConfig, FIREBASE_CLIENT_ID } from '@/config/firebase';
const PREFIX = 'common: hooks: useGooglePicker:';
const {
  createWrappedError
} = utilsCore;
export default function useGooglePicker() {
  const {
    user
  } = useAuth();
  const {
    addToast: sendNotification
  } = useToast();
  const [getPicker] = useDrivePicker();
  const openPicker = useCallback(async google => {
    let isAccessTokenAlive = false;
    let accessToken = google.accessToken;

    try {
      isAccessTokenAlive = await checkAccessToken({
        accessToken: google.accessToken
      });
    } catch (err) {
      logger.error(createWrappedError(`${PREFIX} openPicker: failed to check access token is alive`, err));
    }

    if (!isAccessTokenAlive) {
      try {
        const data = await googleCloudTokenRefresh({
          userUid: user.uid,
          refreshToken: google.refreshToken
        });
        if (data) accessToken = data === null || data === void 0 ? void 0 : data.accessToken;
      } catch (err) {
        logger.error(createWrappedError(`${PREFIX} openPicker: failed to refresh access token`, err));
        sendNotification({
          type: 'error',
          description: 'Failed to refresh access token. Please try again later or log in again.'
        });
      }
    }

    if (!accessToken) {
      logger.error(Error(`${PREFIX} to access any access token for user`));
      sendNotification({
        type: 'error',
        description: 'Unexpected issue. Please try again later or log in again.'
      });
    }

    return new Promise(resolve => {
      getPicker({
        clientId: FIREBASE_CLIENT_ID || '',
        developerKey: firebaseConfig.apiKey || '',
        viewId: 'SPREADSHEETS',
        showUploadView: false,
        showUploadFolders: false,
        supportDrives: false,
        multiselect: false,
        token: accessToken,
        callbackFunction: data => {
          var _data$docs;

          if (data.action === 'picked' && !!(data !== null && data !== void 0 && (_data$docs = data.docs) !== null && _data$docs !== void 0 && _data$docs.length)) {
            var _data$docs2, _data$docs2$, _data$docs3, _data$docs3$, _data$docs4, _data$docs4$;

            resolve({
              fileId: data === null || data === void 0 ? void 0 : (_data$docs2 = data.docs) === null || _data$docs2 === void 0 ? void 0 : (_data$docs2$ = _data$docs2[0]) === null || _data$docs2$ === void 0 ? void 0 : _data$docs2$.id,
              fileName: data === null || data === void 0 ? void 0 : (_data$docs3 = data.docs) === null || _data$docs3 === void 0 ? void 0 : (_data$docs3$ = _data$docs3[0]) === null || _data$docs3$ === void 0 ? void 0 : _data$docs3$.name,
              fileMimeType: data === null || data === void 0 ? void 0 : (_data$docs4 = data.docs) === null || _data$docs4 === void 0 ? void 0 : (_data$docs4$ = _data$docs4[0]) === null || _data$docs4$ === void 0 ? void 0 : _data$docs4$.mimeType,
              fileUserId: google.googleUserId,
              accessToken: accessToken || ''
            });
          } else if (data.action === 'cancel') {
            resolve(null);
          }
        }
      });
    });
  }, [getPicker, sendNotification, user === null || user === void 0 ? void 0 : user.uid]);
  return {
    openPicker
  };
}